import type { ScheduleTemplateItem } from '@/lib/scheduleUtils';
import type { EditableTemplateLink } from './ScheduleTemplateEditorLinksSection';

export interface TemplateTimelineBar {
  item: ScheduleTemplateItem;
  startDay: number;
  duration: number;
  endDay: number;
}

/** Synthetic Day 1…N timeline from sort_order, duration, and FS/SS links. */
export function computeTemplateTimeline(
  items: ScheduleTemplateItem[],
  links: EditableTemplateLink[]
): { bars: TemplateTimelineBar[]; totalDays: number } {
  const sorted = [...items]
    .filter((item) => item.id)
    .sort((a, b) => a.sort_order - b.sort_order);

  const duration = new Map<string, number>();
  const startDay = new Map<string, number>();

  sorted.forEach((item) => {
    if (!item.id) return;
    duration.set(item.id, Math.max(1, item.default_duration_days || 1));
  });

  let cursor = 1;
  for (const item of sorted) {
    if (!item.id) continue;
    startDay.set(item.id, cursor);
    cursor += duration.get(item.id)!;
  }

  const maxPasses = Math.max(sorted.length * 2, links.length + 2, 8);
  for (let pass = 0; pass < maxPasses; pass++) {
    let changed = false;
    for (const link of links) {
      const predStart = startDay.get(link.predecessor_item_id);
      const succId = link.successor_item_id;
      if (predStart == null || !duration.has(succId)) continue;

      const predDur = duration.get(link.predecessor_item_id) ?? 1;
      const lag = link.lag_days ?? 0;
      const requiredStart =
        link.link_type === 'SS' ? predStart + lag : predStart + predDur + lag;
      const currentStart = startDay.get(succId) ?? 1;

      if (requiredStart > currentStart) {
        startDay.set(succId, requiredStart);
        changed = true;
      }
    }
    if (!changed) break;
  }

  const bars: TemplateTimelineBar[] = sorted
    .filter((item) => item.id)
    .map((item) => {
      const start = startDay.get(item.id!) ?? 1;
      const dur = duration.get(item.id!) ?? 1;
      return {
        item,
        startDay: start,
        duration: dur,
        endDay: start + dur - 1,
      };
    });

  const totalDays = bars.reduce((max, bar) => Math.max(max, bar.endDay), 1);

  return { bars, totalDays };
}
